import { Injectable } from '@angular/core';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { HttpHeaders } from '@angular/common/http';
import { AppStatus } from './interfaces/app-status.interface';
import { PurlSearchParams } from './interfaces/purl-search-params.interface';
import { environment } from 'src/environments/environment';
import { PurlCount } from './interfaces/purl-count-response.interface';
import { PurlSearchResponse } from './interfaces/purl-search-response.interface';
import { ClientListResponse } from './interfaces/login-as-employer.interface';
import { EnvironmentConfig } from '../../../environments/environment.interface';
import { SSOExtraDataRequestPayload, SSOPrimerRequestPayload, SSOPrimerResponse } from './interfaces/sso-primer.interface';

const _environment: EnvironmentConfig = environment;

@Injectable({
  providedIn: 'root'
})
export class ProducerService {

  constructor(
    private httpClient: HTTPWrapperContext
  ) { }

  getApplicationStatusCount(userId: string, marketType: string, agencyId?: string): Promise<AppStatus> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v4/retailapp/appStatusCount',
      headers: new HttpHeaders({
        // Use the global ASP ddtoken to pool together all ASP sub-brands
        ddToken: btoa(_environment.initData.brand.ddToken),
        productType: marketType,
      }),
      POST_OR_PUT_PAYLOAD: {},
    });
    if (agencyId) {
      requestConfig.headers = requestConfig.headers.set('agencyId', agencyId);
    }
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async updateAgencyPref(loginIdLocal: string, agencyIdLocal: string, regionLocal: string): Promise<any> {
    const payload = {
      payload: {
        loginId: loginIdLocal,
        agencyId: agencyIdLocal,
        region: regionLocal
      }
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        ddToken: btoa(_environment.initData.brand.ddToken),
      }),
      POST_OR_PUT_PAYLOAD: payload,
      url: '/sxesvc/api/v4/userInfo/updateAgencyPref'
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  getPurlStatusCount(agencyId?: string): Promise<PurlCount[]> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v3/referralApp/statusCount',
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: {},
    });
    if (agencyId) {
      requestConfig.headers = requestConfig.headers.set('agencyId', agencyId);
    }
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  getReferrals(searchParams: PurlSearchParams): Promise<PurlSearchResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v3/referralApp/retrieve',
      headers: new HttpHeaders({}),
      POST_OR_PUT_PAYLOAD: searchParams
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  getLoginAsEmployerClientLists(agencyId: string, loginId: string): Promise<ClientListResponse> {
    let url = `/cmcrst/x-services/currentClientList`;
    const requestConfig = new RequestConfigImplementation({
      url: url,
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
      }),
       POST_OR_PUT_PAYLOAD: {
        "searchFor": "potentialClientList",
        "brokerNumber": agencyId,
        "loginId": loginId
       }
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfig);
  }

  /**
  @description 
    Submits a request with agency values that are stored as the default values for this user.
    These values can then be used by the backend for the next SSO call to the saml gateway, allowing us
    to customize what values are relayed to the actual SSO vendor.
  */
 ssoPrimerRequest(payload: SSOPrimerRequestPayload): Promise<SSOPrimerResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.PUT,                                     //  PUT
      url: '/secapi/v2/ext/userServices/modifyUserByAttributes',      //  secapi + v2
      headers: new HttpHeaders({}),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.disableErrorSnackbar(requestConfig);
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  /**
  @description 
    This service is a workaround for Connecture not consuming the data correctly from the ssoPrimerRequest. This adds additional
    agent details so the user sees the correct data after they go over to Connecture.
    We can possibly remove this in the future if they fix the issue on their end
  */
  ssoExtraDataRequest(payload: SSOExtraDataRequestPayload): Promise<SSOExtraDataRequestPayload> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.PUT,
      url: `/connecture-agent/v3/ext/Agents/${payload.AgentID}`,
      headers: new HttpHeaders({}),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.disableErrorSnackbar(requestConfig);
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }
}
